import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faUtensils,
  faUsers,
  faChartLine,
  faSpinner,
  faCalendarAlt,
  faCheckCircle,
  faClock,
  faSave,
  faTimes,
  faPencilAlt
} from '@fortawesome/free-solid-svg-icons';
import { db } from '../../firebase';
import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { Link } from 'react-router-dom';

const StatCard = ({ title, value, icon, trend, link, color = "blue" }) => (
  <Link to={link} className="block">
    <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-all">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <div className="flex items-baseline">
            <p className="text-2xl font-semibold text-gray-900">{value}</p>
            {trend && (
              <span className={`ml-2 text-sm font-medium ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {trend > 0 ? '+' : ''}{trend}%
              </span>
            )}
          </div>
        </div>
        <div className={`rounded-full p-3 bg-${color}-100`}>
          <FontAwesomeIcon icon={icon} className={`h-6 w-6 text-${color}-600`} />
        </div>
      </div>
    </div>
  </Link>
);

const RecentActivityCard = ({ title, items }) => (
  <div className="bg-white rounded-lg shadow-sm p-6">
    <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
    {items.length === 0 ? (
      <p className="text-gray-500 text-center py-4">No recent activity</p>
    ) : (
      <div className="space-y-4">
        {items.map((item, index) => (
          <div key={index} className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`rounded-full p-2 bg-${item.color}-100 mr-3`}>
                <FontAwesomeIcon icon={item.icon} className={`h-4 w-4 text-${item.color}-600`} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{item.title}</p>
                <p className="text-xs text-gray-500">{item.timestamp}</p>
              </div>
            </div>
            <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${item.statusColor}`}>
              {item.status}
            </span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const Dashboard = () => {
  const [metrics, setMetrics] = useState({
    contactSubmissions: { total: 0, pending: 0, trend: 0 },
    cateringRequests: { total: 0, pending: 0, trend: 0 },
    bccEmails: { total: 0, active: 0 },
    isLoading: true
  });
  const [recentActivity, setRecentActivity] = useState([]);

  const adminLinks = [
    {
      title: 'Services',
      description: 'Manage restaurant services',
      icon: faUtensils,
      path: '/admin/services'
    },
  ];

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Get current date and date 30 days ago
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));

      // Fetch contact submissions
      const contactSnap = await getDocs(collection(db, "contactSubmissions"));
      const contactTotal = contactSnap.size;
      const contactPending = contactSnap.docs.filter(doc => 
        doc.data().status === 'new' || doc.data().status === 'inProgress'
      ).length;

      // Fetch catering requests
      const cateringSnap = await getDocs(collection(db, "cateringFormSubmissions"));
      const cateringTotal = cateringSnap.size;
      const cateringPending = cateringSnap.docs.filter(doc => !doc.data().emailed).length;

      // Fetch BCC emails
      const bccSnap = await getDocs(collection(db, "bccEmails"));
      const bccTotal = bccSnap.size;
      const bccActive = bccSnap.docs.filter(doc => doc.data().enabled).length;

      // Get recent activities
      const recentActivities = [
        ...contactSnap.docs.map(doc => ({
          type: 'contact',
          data: doc.data(),
          id: doc.id,
          timestamp: doc.data().date?.toDate() || new Date()
        })),
        ...cateringSnap.docs.map(doc => ({
          type: 'catering',
          data: doc.data(),
          id: doc.id,
          timestamp: doc.data().createdAt?.toDate() || new Date()
        }))
      ]
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 5)
      .map(activity => ({
        icon: activity.type === 'contact' ? faEnvelope : faUtensils,
        color: activity.type === 'contact' ? 'blue' : 'green',
        title: `${activity.data.name} - ${activity.type === 'contact' ? 'Contact Form' : 'Catering Request'}`,
        timestamp: activity.timestamp.toLocaleDateString(),
        status: activity.type === 'contact' ? activity.data.status || 'new' : (activity.data.emailed ? 'Processed' : 'Pending'),
        statusColor: getStatusColor(activity)
      }));

      setMetrics({
        contactSubmissions: { 
          total: contactTotal, 
          pending: contactPending,
          trend: calculateTrend(contactSnap.docs, thirtyDaysAgo)
        },
        cateringRequests: { 
          total: cateringTotal, 
          pending: cateringPending,
          trend: calculateTrend(cateringSnap.docs, thirtyDaysAgo)
        },
        bccEmails: { total: bccTotal, active: bccActive },
        isLoading: false
      });

      setRecentActivity(recentActivities);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setMetrics(prev => ({ ...prev, isLoading: false }));
    }
  };

  const calculateTrend = (docs, thirtyDaysAgo) => {
    const recentDocs = docs.filter(doc => 
      (doc.data().date?.toDate() || doc.data().createdAt?.toDate() || new Date()) > thirtyDaysAgo
    ).length;
    const totalDocs = docs.length;
    return totalDocs ? Math.round((recentDocs / totalDocs) * 100) : 0;
  };

  const getStatusColor = (activity) => {
    if (activity.type === 'contact') {
      switch (activity.data.status) {
        case 'completed': return 'bg-green-100 text-green-800';
        case 'inProgress': return 'bg-yellow-100 text-yellow-800';
        default: return 'bg-blue-100 text-blue-800';
      }
    }
    return activity.data.emailed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800';
  };

  if (metrics.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FontAwesomeIcon icon={faSpinner} spin className="text-blue-500 text-4xl" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Dashboard Overview</h1>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <StatCard
          title="Contact Submissions"
          value={metrics.contactSubmissions.total}
          icon={faEnvelope}
          trend={metrics.contactSubmissions.trend}
          link="/admin/contact-submissions"
          color="blue"
        />
        <StatCard
          title="Catering Requests"
          value={metrics.cateringRequests.total}
          icon={faUtensils}
          trend={metrics.cateringRequests.trend}
          link="/admin/catering-requests"
          color="green"
        />
        <StatCard
          title="Active BCC Emails"
          value={`${metrics.bccEmails.active}/${metrics.bccEmails.total}`}
          icon={faUsers}
          link="/admin/bcc-emails"
          color="purple"
        />
      </div>

      {/* Pending Actions and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Actions */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Pending Actions</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faEnvelope} className="text-yellow-600 mr-3" />
                <div>
                  <p className="font-medium text-yellow-900">Contact Forms</p>
                  <p className="text-sm text-yellow-700">{metrics.contactSubmissions.pending} pending responses</p>
                </div>
              </div>
              <Link 
                to="/admin/contact-submissions"
                className="text-sm text-yellow-700 hover:text-yellow-900 font-medium"
              >
                View →
              </Link>
            </div>
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faUtensils} className="text-green-600 mr-3" />
                <div>
                  <p className="font-medium text-green-900">Catering Requests</p>
                  <p className="text-sm text-green-700">{metrics.cateringRequests.pending} unprocessed requests</p>
                </div>
              </div>
              <Link 
                to="/admin/catering-requests"
                className="text-sm text-green-700 hover:text-green-900 font-medium"
              >
                View →
              </Link>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <RecentActivityCard 
          title="Recent Activity"
          items={recentActivity}
        />
      </div>
    </div>
  );
};

export default Dashboard; 